/**
 * Cart Initialling — service/parent linking.
 *
 * When a customer adds a personalisation service (e.g. "Service · Initialling")
 * alongside a luggage/product item, the service line carries:
 *   data-linked-title = parent product's title (via item.properties.Product)
 * The parent product line carries:
 *   data-line-title   = its own item.title
 *   item.properties.Initials / Position / Size / Font / Colour, listed in the
 *   hidden item.properties._personalisation_keys marker (comma-joined)
 *
 * The "Product" key and the clean Initials/Position/... labels are set by
 * assets/ATC3dium.js, renamed from Threedium's raw "Title" / "Personalisation_*"
 * keys at add-to-cart time so Shopify's hosted Checkout, order-status page, and
 * order confirmation emails (none of which run our theme's Liquid) show clean
 * labels instead of raw underscored property names. "Product" is deliberately
 * left visible (not underscore-hidden) on those surfaces — it tells the
 * customer/staff which product the service belongs to, same as Shopify's
 * default behaviour before this cleanup. LEGACY_PERSONALISATION_PREFIX and the
 * "_Title"/"Title" fallback checks below exist only so carts started before
 * this rename (or the earlier "_Title" version of it) keep working through
 * the transition (see isPersonalisationKey / the service-item lookup).
 *
 * This script keeps the two lines in sync on removal:
 *
 *   FORWARD  service line removed → clear parent's personalisation props
 *            (so the personalisation doesn't linger orphaned)
 *
 *   REVERSE  parent product removed → also remove its attached service line
 *            (so the customer isn't charged for personalisation alone)
 *
 * Matching is done by item.properties._pairing_id (set by ATC3dium.js on both
 * lines of a pair at add-to-cart time) plus each line's own unique item.key —
 * exposed on the DOM row as data-pairing-id / data-line-key. Title alone is
 * NOT a safe match key: with the same product added to the cart twice (e.g.
 * once with personalisation removed, once freshly added with new initials),
 * two rows share the same data-line-title, and matching by title alone can
 * grab the wrong row's service line. _pairing_id is random per add-to-cart
 * event, so it disambiguates even between identical-title lines.
 *
 * Falls back to the legacy title-based match (data-linked-title / item.title)
 * only when data-pairing-id is empty — carts started before this pairing-id
 * fix shipped won't have the property on their line items yet.
 *
 * Both listeners are fail-safe: any error falls back to letting the theme's
 * native remove behaviour run, so removal is never silently swallowed.
 */
(function () {
  'use strict';

  var REMOVE_SELECTOR = '[is="cart-remove-item"]';
  var SERVICE_CLASS = 'cart-item--service';
  var LEGACY_PERSONALISATION_PREFIX = 'Personalisation_';

  // True if `key` is one of a line item's personalisation properties — either
  // the new short label (listed in the item's own _personalisation_keys marker)
  // or, for carts added before the ATC3dium.js rename shipped, the old
  // "Personalisation_*"-prefixed key.
  function isPersonalisationKey(properties, key) {
    if (key === '_personalisation_keys') return true;
    if (key.indexOf(LEGACY_PERSONALISATION_PREFIX) === 0) return true;
    var marker = properties && properties._personalisation_keys;
    return !!marker && marker.split(',').indexOf(key) !== -1;
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed: ' + r.status);
      return r.json();
    });
  }

  function changeLine(id, payload) {
    var body = Object.assign({ id: id }, payload);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart change failed: ' + r.status);
      return r.json();
    });
  }

  function refreshCart() {
    // Try the theme's web-component refresh if exposed; otherwise dispatch a
    // synthetic cart:refresh event that the theme cart drawer listens for.
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      // dawn-style drawer
      try { drawer.renderContents({ sections: null }); } catch (_) {}
    }
    // As a last resort the theme will re-render on next add or page change.
  }

  function findServiceLineFor(parentTitle) {
    if (!parentTitle) return null;
    var services = document.querySelectorAll('.cart-item.' + SERVICE_CLASS);
    for (var i = 0; i < services.length; i++) {
      if (services[i].dataset.linkedTitle === parentTitle) return services[i];
    }
    return null;
  }

  function findParentLineFor(parentTitle) {
    if (!parentTitle) return null;
    var lines = document.querySelectorAll('.cart-item:not(.' + SERVICE_CLASS + ')');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].dataset.lineTitle === parentTitle) return lines[i];
    }
    return null;
  }

  // Unique per add-to-cart pairing event — safe even when two cart rows share
  // the same product title (see file header). Used as a cheap sync DOM check
  // before we bother hitting /cart.js for the authoritative match.
  function findLineWithPairingId(pairingId, excludeLine) {
    if (!pairingId) return null;
    var lines = document.querySelectorAll('.cart-item[data-pairing-id="' + pairingId + '"]');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i] !== excludeLine) return lines[i];
    }
    return null;
  }

  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest ? event.target.closest(REMOVE_SELECTOR) : null;
    if (!btn) return;

    var line = btn.closest('.cart-item');
    if (!line) return;

    var isService = line.classList.contains(SERVICE_CLASS);

    if (isService) {
      // FORWARD — service line being removed.
      // Let the theme remove the service normally; we just clear the parent's
      // Personalisation_* props afterwards. This is asynchronous and best-effort —
      // never block the user's remove click.
      var serviceKey = line.dataset.lineKey;
      var servicePairingId = line.dataset.pairingId;
      var parentTitle = line.dataset.linkedTitle;
      if (!servicePairingId && !parentTitle) return;

      // Fire-and-forget after a short delay so the theme's removal POST goes first.
      setTimeout(function () {
        fetchCart().then(function (cart) {
          var items = cart.items || [];
          // _pairing_id is exact — safe even if another line shares this title.
          // Match "the other line in this pair" by excluding this service's own
          // key, NOT by _pairing_role: ATC3dium.js's role detection only
          // recognises the full bespoke-customiser attribute names (Body/Frame/
          // Hardware/Wheels/...); the simple Initials-only popup never sends
          // those, so BOTH lines of a pair can end up tagged "initialling" —
          // filtering on role would silently fail to find the parent here.
          // Falls back to title only for carts started before _pairing_id existed.
          var parent = servicePairingId
            ? items.find(function (it) { return it.key !== serviceKey && it.properties && it.properties._pairing_id === servicePairingId; })
            : items.find(function (it) { return it.title === parentTitle; });
          if (!parent || !parent.properties) return;

          var newProps = {};
          var changed = false;
          Object.keys(parent.properties).forEach(function (k) {
            if (isPersonalisationKey(parent.properties, k)) {
              // Only counts as a real change if there was a non-blank value to
              // strip — an already-stripped line keeps blank-valued keys rather
              // than dropping them (see hasPersonalisation below); re-sending
              // the same no-op strip would just be a wasted request.
              if (parent.properties[k] !== '') changed = true;
            } else {
              newProps[k] = parent.properties[k];
            }
          });
          if (!changed) return;

          return changeLine(parent.key, { properties: newProps }).then(refreshCart);
        }).catch(function (err) {
          console.warn('[cart-initialling] forward sync failed:', err);
        });
      }, 250);

      return; // do not preventDefault — let theme handle the service removal
    }

    // REVERSE — non-service line (the parent product) being removed.
    // If a matching service line exists AND this parent carries Personalisation_*
    // props, we take over: remove both lines via cart-change, then refresh.

    var parentKey = line.dataset.lineKey;
    var pairingId = line.dataset.pairingId;
    var lineTitle = line.dataset.lineTitle;

    // Cheap DOM check first: _pairing_id is exact (won't grab a same-titled line's
    // service by mistake); title match is only the fallback for legacy cart lines
    // added before _pairing_id existed.
    var pairedLine = pairingId ? findLineWithPairingId(pairingId, line) : findServiceLineFor(lineTitle);
    if (!pairedLine) return; // nothing attached — let theme do its thing

    // Intercept the click — we'll handle the dual-remove ourselves.
    event.preventDefault();
    event.stopPropagation();

    fetchCart().then(function (cart) {
      var items = cart.items || [];
      // Re-locate the authoritative record. By exact key when we have one (always,
      // for lines rendered after this fix); title only as a legacy fallback.
      var parent = parentKey
        ? items.find(function (it) { return it.key === parentKey; })
        : items.find(function (it) { return it.title === lineTitle; });
      if (!parent) throw new Error('parent not found in cart');

      // Value must be non-blank, not just the key present: a line that already
      // had its personalisation stripped keeps the key names (e.g. Initials: "",
      // _personalisation_keys: "") rather than dropping them outright — checking
      // key presence alone treats that leftover blank marker as "still
      // personalised" and would wrongly drag a still-active sibling's service
      // into the removal (see custom-filter ticket: two lines sharing a title).
      var hasPersonalisation = parent.properties && Object.keys(parent.properties).some(function (k) {
        return isPersonalisationKey(parent.properties, k) && parent.properties[k] !== '';
      });

      // If parent doesn't carry personalisation props (e.g. a duplicate non-personalised
      // copy of the same product), leave the service alone — just remove the parent.
      if (!hasPersonalisation) {
        return changeLine(parent.key, { quantity: 0 }).then(refreshCart);
      }

      // Find the matching service line — by exact _pairing_id (immune to duplicate
      // titles in the cart), or by linked title (its Product property — or "_Title"/
      // legacy "Title") only when this line predates the _pairing_id fix.
      var serviceItem = pairingId
        ? items.find(function (it) { return it.key !== parent.key && it.properties && it.properties._pairing_id === pairingId; })
        : items.find(function (it) {
            return it.properties && (
              it.properties.Product === lineTitle ||
              it.properties._Title === lineTitle ||
              it.properties.Title === lineTitle
            );
          });
      if (!serviceItem) {
        // Service vanished between DOM render and now — just remove parent.
        return changeLine(parent.key, { quantity: 0 }).then(refreshCart);
      }

      // Remove service first, then parent. Using line keys so order doesn't drift.
      return changeLine(serviceItem.key, { quantity: 0 })
        .then(function () { return changeLine(parent.key, { quantity: 0 }); })
        .then(refreshCart);
    }).catch(function (err) {
      console.warn('[cart-initialling] reverse remove failed, falling back to native remove:', err);
      // Re-trigger the original click without our listener interfering.
      btn.removeEventListener('click', arguments.callee);
      btn.click();
    });
  }, true); // capture phase — fires before theme's default handler so we can intercept
})();

// Newsletter modal — stop it re-opening on every refresh after a successful submit.
// theme.js (NewsletterForm) opens the modal when the URL still carries the form-success
// params; that state persists across refreshes, so closing it doesn't stick. This script
// loads after theme.js (deferred, end of body), so the modal is already shown — we just
// strip the success params from the URL via history.replaceState (the facets.js pattern).
// A refresh then loads a clean URL → no posted state → modal stays closed. A NEW submit
// re-adds the params, so a genuine re-subscribe still shows the modal (correct).
(function () {
  'use strict';
  if (!document.querySelector('.modal--newsletter-alert')) return; // only on pages with the modal
  if (!window.isNewsletterModalShow) return; // theme.js sets this only when it opened the modal
  try {
    var url = new URL(window.location.href);
    var dirty = false;
    ['customer_posted', 'form_type', 'contact_posted'].forEach(function (p) {
      if (url.searchParams.has(p)) { url.searchParams.delete(p); dirty = true; }
    });
    if (dirty) history.replaceState({}, '', url.toString());
  } catch (e) {
    /* no-op: URL/History unavailable — falls back to existing behaviour */
  }
})();
